import type { Bootstrap, Spend, AnalysisResult, Bill, SavingsGoal, PositionResult, BudgetResult, ActivityResult, BalancesResult, EstimatorResult } from "../../shared/types";

// Thin fetch wrapper for the /api/* layer. Every response is JSON; non-2xx
// responses carry an { error } body.
async function getJson<T>(path: string): Promise<T> {
  // `no-store` so the browser never serves a stale cached copy — e.g. after
  // updating a salary, the next bootstrap must reflect it, not a cached one.
  let res: Response;
  try {
    res = await fetch(path, { headers: { accept: "application/json" }, cache: "no-store" });
  } catch {
    // fetch only rejects for network-level failures (offline, DNS, dropped
    // connection) — never for a 4xx/5xx. Give a human message.
    throw new Error("Couldn't reach the server — check your internet connection.");
  }
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // non-JSON error body; keep the generic message
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

async function sendJson<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: { "content-type": "application/json", accept: "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // Network-level failure (offline / dropped connection) — the change did NOT
    // save. Say so plainly so the form can surface it.
    throw new Error("Couldn't save — you appear to be offline. Your change hasn't been saved.");
  }
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) message = b.error;
    } catch {
      // keep generic
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

export interface NewSpend {
  occurredOn: string;
  where: string;
  amountMinor: number;
  categoryId: number | null;
  paidBy: number | null;
  offBalance: boolean;
  kind?: "out" | "in";
  budgetItemId?: number | null;
  note?: string | null;
}

export interface BudgetInput {
  name: string;
  amountMinor: number;
  cadence: "weekly" | "monthly" | "once";
  categoryId: number | null;
  paidBy: number | null;
  notes: string | null;
}

export interface BillInput {
  name: string;
  amountMinor: number;
  type: string;
  frequency: string;
  categoryId: number | null;
  paidBy: number | null;
  dueDay: number | null;
  accountNumber: string | null;
  website: string | null;
  notes: string | null;
  startDate: string | null;
  endDate: string | null;
}

export interface AccountInput {
  memberId: number;
  amountMinor: number;
}

export interface PlanInput {
  name: string;
  provider: string | null;
  categoryId: number | null;
  paidBy: number | null;
  amountMinor: number;
  firstAmountMinor: number | null;
  lastAmountMinor: number | null;
  numPayments: number;
  frequency: string;
  startPeriod: string;
  dueDay: number | null;
}

export interface IncomeInput {
  memberId: number;
  amountMinor: number;
  payDay: number | null;
}

export interface SavingInput {
  name: string;
  targetMinor: number;
  currentMinor: number;
  startDate: string | null;
  targetDate: string | null;
  description: string | null;
  memberId: number | null;
  bank: string | null;
}

export interface ManagedMember {
  id: number;
  name: string;
  initial: string;
  colour: string;
  username: string | null;
  hasLogin: boolean;
}

export interface MemberInput {
  name: string;
  initial?: string;
  colour?: string;
  username?: string;
  password?: string;
}

export const api = {
  // --- auth ---
  authStatus: () => getJson<{ needsSetup: boolean; authed: boolean; member: string | null }>("/api/auth/status"),
  login: (username: string, password: string) =>
    sendJson<{ ok: boolean; member: string }>("POST", "/api/auth/login", { username, password }),
  setup: (setupCode: string, name: string, username: string, password: string) =>
    sendJson<{ ok: boolean; member: string }>("POST", "/api/auth/setup", { setupCode, name, username, password }),
  logout: () => sendJson<{ ok: boolean }>("POST", "/api/auth/logout"),

  // --- people & logins (Settings) ---
  listMembers: () => getJson<{ members: ManagedMember[] }>("/api/members"),
  createMember: (m: MemberInput) => sendJson<{ ok: boolean; id: number }>("POST", "/api/members", m),
  updateMember: (id: number, m: Partial<MemberInput>) => sendJson<{ ok: boolean }>("PATCH", `/api/members/${id}`, m),
  deleteMember: (id: number) => sendJson<{ ok: boolean }>("DELETE", `/api/members/${id}`),

  bootstrap: (period?: string) =>
    getJson<Bootstrap>("/api/bootstrap" + (period ? `?period=${period}` : "")),
  activity: (opts?: {
    limit?: number; offset?: number; member?: number | null; money?: boolean;
    from?: string | null; to?: string | null; amin?: number | null; amax?: number | null;
    type?: string | null; category?: number | null;
  }) => {
    const p = new URLSearchParams();
    if (opts?.limit) p.set("limit", String(opts.limit));
    if (opts?.offset) p.set("offset", String(opts.offset));
    if (opts?.member != null) p.set("member", String(opts.member));
    if (opts?.money) p.set("money", "1");
    if (opts?.from) p.set("from", opts.from);
    if (opts?.to) p.set("to", opts.to);
    if (opts?.amin != null) p.set("amin", String(opts.amin));
    if (opts?.amax != null) p.set("amax", String(opts.amax));
    if (opts?.type) p.set("type", opts.type);
    if (opts?.category != null) p.set("category", String(opts.category));
    const qs = p.toString();
    return getJson<ActivityResult>("/api/activity" + (qs ? `?${qs}` : ""));
  },
  position: (period?: string) =>
    getJson<PositionResult>("/api/position" + (period ? `?period=${period}` : "")),
  analysis: (scope: "month" | "year" | "all", period?: string) =>
    getJson<AnalysisResult>(`/api/analysis?scope=${scope}` + (period ? `&period=${period}` : "")),
  tick: (billId: number, period: string, paid: boolean, occ = "") =>
    sendJson<{ ok: boolean }>("POST", "/api/ticks", { billId, period, paid, occ }),
  tickAll: (period: string, paid: boolean) =>
    sendJson<{ ok: boolean }>("POST", "/api/ticks/all", { period, paid }),
  listSpends: (period: string) =>
    getJson<{ spends: Spend[] }>(`/api/spends?period=${period}`),
  createSpend: (spend: NewSpend) =>
    sendJson<{ ok: boolean; id: number }>("POST", "/api/spends", spend),
  updateSpend: (id: number, spend: NewSpend) =>
    sendJson<{ ok: boolean }>("PATCH", `/api/spends/${id}`, spend),
  scanReceipt: (image: string) =>
    sendJson<{ amountMinor: number | null; where: string | null; date: string | null }>("POST", "/api/receipt", { image }),
  deleteSpend: (id: number) =>
    sendJson<{ ok: boolean }>("DELETE", `/api/spends/${id}`),
  setBalance: (asOf: string, accounts: AccountInput[]) =>
    sendJson<{ ok: boolean }>("POST", "/api/balance", { asOf, accounts }),
  balances: () => getJson<BalancesResult>("/api/balances"),
  listBills: () => getJson<{ bills: Bill[] }>("/api/bills"),
  createBill: (bill: BillInput) =>
    sendJson<{ ok: boolean; id: number }>("POST", "/api/bills", bill),
  setTrackerName: (trackerName: string) =>
    sendJson<{ ok: boolean }>("POST", "/api/settings", { trackerName }),
  updateBill: (id: number, bill: BillInput) =>
    sendJson<{ ok: boolean }>("PATCH", `/api/bills/${id}`, bill),
  deleteBill: (id: number) =>
    sendJson<{ ok: boolean }>("DELETE", `/api/bills/${id}`),
  restoreBill: (id: number) =>
    sendJson<{ ok: boolean }>("POST", `/api/bills/${id}`),
  saveIncome: (entries: IncomeInput[]) =>
    sendJson<{ ok: boolean }>("POST", "/api/income", { entries }),
  createPlan: (plan: PlanInput) =>
    sendJson<{ ok: boolean; id: number }>("POST", "/api/plans", plan),
  updatePlan: (id: number, plan: PlanInput) =>
    sendJson<{ ok: boolean }>("PATCH", `/api/plans/${id}`, plan),
  deletePlan: (id: number) =>
    sendJson<{ ok: boolean }>("DELETE", `/api/plans/${id}`),
  restorePlan: (id: number) =>
    sendJson<{ ok: boolean }>("POST", `/api/plans/${id}`),
  estimator: (fromPeriod?: string, months = 12) =>
    getJson<EstimatorResult>(`/api/estimator?months=${months}${fromPeriod ? `&from=${fromPeriod}` : ""}`),
  listBudget: () => getJson<BudgetResult>("/api/budget"),
  createBudget: (b: BudgetInput) => sendJson<{ ok: boolean; id: number }>("POST", "/api/budget", b),
  updateBudget: (id: number, b: BudgetInput) => sendJson<{ ok: boolean }>("PATCH", `/api/budget/${id}`, b),
  deleteBudget: (id: number) => sendJson<{ ok: boolean }>("DELETE", `/api/budget/${id}`),
  listSavings: () => getJson<{ goals: SavingsGoal[] }>("/api/savings"),
  createSaving: (g: SavingInput) => sendJson<{ ok: boolean; id: number }>("POST", "/api/savings", g),
  updateSaving: (id: number, g: SavingInput) => sendJson<{ ok: boolean }>("PATCH", `/api/savings/${id}`, g),
  deleteSaving: (id: number) => sendJson<{ ok: boolean }>("DELETE", `/api/savings/${id}`),
  tickPlan: (planId: number, seq: number, paid: boolean) =>
    sendJson<{ ok: boolean }>("POST", `/api/plans/${planId}/tick`, { seq, paid }),
  setPayPeriod: (period: string, startDate: string, endDate: string) =>
    sendJson<{ ok: boolean }>("POST", "/api/pay-period", { period, startDate, endDate }),
  createCategory: (name: string, colour: string) =>
    sendJson<{ ok: boolean; id: number }>("POST", "/api/categories", { name, colour }),
  updateCategory: (id: number, name: string, colour: string) =>
    sendJson<{ ok: boolean }>("PATCH", `/api/categories/${id}`, { name, colour }),
  deleteCategory: (id: number) =>
    sendJson<{ ok: boolean }>("DELETE", `/api/categories/${id}`),
  createType: (label: string, description: string, colour: string) =>
    sendJson<{ ok: boolean; key: string }>("POST", `/api/types`, { label, description, colour }),
  updateType: (key: string, label: string, description: string, colour: string) =>
    sendJson<{ ok: boolean }>("PATCH", `/api/types/${key}`, { label, description, colour }),
  deleteType: (key: string) =>
    sendJson<{ ok: boolean }>("DELETE", `/api/types/${key}`),
};
